import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import { fmtDate, fmtMoney, fmtNumber } from '../lib/format';
import { Button, Empty, ErrorState, Ltr, SkeletonRows, useToast } from './ui';
import { useOpenDocument } from './Layout';
import { whatsappNumber } from './IssueOrder';

type State = 'waiting' | 'approved' | 'invoiced' | 'replaced' | 'rejected' | 'done';
type Tracked = {
  documentId: number;
  number: string | null;
  issueDate: string;
  total: number | null;
  customerId: number;
  customerName: string;
  phone: string | null;
  email: string | null;
  orderId: number | null;
  morningUrl: string | null;
  state: State;
  approvedAt: string | null;
  remindedAt: string | null;
  reminders: number;
  note: string | null;
  daysOpen: number;
  daysSinceContact: number;
  remindDue: boolean;
  fulfilled: Array<{ orderId: number; type: string; number: string | null; documentId: number | null }>;
  replacedBy: Array<{ orderId: number; number: string | null; documentId: number | null }>;
  laterSale: string | null;
};
type TrackedList = {
  items: Tracked[];
  remindAfterDays: number;
  counts: { waiting: number; approved: number; invoiced: number; closed: number; remindDue: number };
  totals: { waiting: number; approved: number };
};
type Tab = 'waiting' | 'approved' | 'invoiced' | 'closed';

const DOC_LABEL: Record<string, string> = { tax_invoice: 'חשבונית מס', tax_invoice_receipt: 'חשבונית מס/קבלה', delivery_note: 'תעודת משלוח', quote: 'הצעת מחיר' };

/** The reminder text sent to the customer (WhatsApp / copied). */
export function reminderText(q: Tracked) {
  return [
    `שלום ${q.customerName},`,
    `רציתי לוודא שקיבלתם את הצעת המחיר${q.number ? ` מס׳ ${q.number}` : ''} מ-${fmtDate(q.issueDate)} על סך ${fmtMoney(q.total)} (כולל מע״מ).`,
    q.morningUrl ? `לצפייה בהצעה: ${q.morningUrl}` : null,
    'נשמח לאשר ולהתקדם בהזמנה, ואם צריך שינוי — נעדכן מיד.',
    'מרקיט ציוד משרדי',
  ]
    .filter(Boolean)
    .join('\n');
}

/**
 * Quotes that went out, until each one is approved and invoiced: approve, remind (WhatsApp),
 * turn into an invoice with the same lines, or into an updated quote.
 */
export function QuoteTracker({ customerId, showCustomer }: { customerId?: number; showCustomer?: boolean }) {
  const { data, error, refetch, isLoading } = useQuery({
    queryKey: ['quote-tracking', customerId ?? 'all'],
    queryFn: () => api<TrackedList>(`/api/orders/quotes${qs({ customerId })}`),
  });
  const [tab, setTab] = useState<Tab>('waiting');
  const [busy, setBusy] = useState<string | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const openDoc = useOpenDocument();

  const act = async (q: Tracked, action: 'approve' | 'reject' | 'done' | 'reopen' | 'remind', ok: string) => {
    setBusy(`${q.documentId}:${action}`);
    try {
      await api(`/api/orders/quotes/${q.documentId}/action`, { body: { action } });
      qc.invalidateQueries({ queryKey: ['quote-tracking'] });
      qc.invalidateQueries({ queryKey: ['workplan'] });
      toast.success(ok);
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };
  const convert = async (q: Tracked, documentType: 'quote' | 'tax_invoice' | 'tax_invoice_receipt' | 'delivery_note') => {
    setBusy(`${q.documentId}:convert`);
    try {
      const r = await api<{ orderId: number }>(`/api/orders/quotes/${q.documentId}/convert`, { body: { documentType } });
      qc.invalidateQueries({ queryKey: ['orders'] });
      navigate(`/orders/${r.orderId}`);
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };
  // The customer said yes: mark it, and open the tax invoice draft with the same lines right away.
  const approveAndInvoice = async (q: Tracked) => {
    setBusy(`${q.documentId}:approve`);
    try {
      await api(`/api/orders/quotes/${q.documentId}/action`, { body: { action: 'approve' } });
      qc.invalidateQueries({ queryKey: ['quote-tracking'] });
    } catch (e) {
      toast.error(e);
      setBusy(null);
      return;
    }
    await convert(q, 'tax_invoice');
  };
  const remind = async (q: Tracked, how: 'whatsapp' | 'copy' | 'phone') => {
    const text = reminderText(q);
    if (how === 'whatsapp') {
      const n = whatsappNumber(q.phone);
      window.open(`https://wa.me/${n ?? ''}?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
    } else if (how === 'copy') {
      await navigator.clipboard.writeText(text).catch(() => undefined);
    }
    await act(q, 'remind', how === 'copy' ? 'הטקסט הועתק — התזכורת נרשמה' : 'התזכורת נרשמה');
  };

  if (error) return <ErrorState error={error} onRetry={() => refetch()} />;
  const items = (data?.items ?? []).filter((i) =>
    tab === 'closed' ? i.state === 'replaced' || i.state === 'rejected' || i.state === 'done' : i.state === tab,
  );
  // Reminders due first, then the oldest.
  if (tab === 'waiting') items.sort((a, b) => Number(b.remindDue) - Number(a.remindDue) || b.daysOpen - a.daysOpen);
  const c = data?.counts;
  const cols = showCustomer ? 6 : 5;

  return (
    <>
      <div className="panel-head row wrap" style={{ gap: 10 }}>
        <h2>הצעות מחיר שיצאו</h2>
        <div className="segmented" role="group" aria-label="סטטוס">
          <button className={tab === 'waiting' ? 'active' : ''} onClick={() => setTab('waiting')}>
            ממתינות לאישור {c ? `(${c.waiting})` : ''}
          </button>
          <button className={tab === 'approved' ? 'active' : ''} onClick={() => setTab('approved')}>
            אושרו — להפקה {c ? `(${c.approved})` : ''}
          </button>
          <button className={tab === 'invoiced' ? 'active' : ''} onClick={() => setTab('invoiced')}>
            הופקו {c ? `(${c.invoiced})` : ''}
          </button>
          <button className={tab === 'closed' ? 'active' : ''} onClick={() => setTab('closed')}>
            סגורות {c ? `(${c.closed})` : ''}
          </button>
        </div>
        <span className="spacer" />
        {data ? (
          <span className="small muted">
            {c!.remindDue ? <span style={{ color: 'var(--warn)' }}>{fmtNumber(c!.remindDue)} לתזכורת · </span> : null}
            ממתינות {fmtMoney(data.totals.waiting, { round: true })} · אושרו {fmtMoney(data.totals.approved, { round: true })}
          </span>
        ) : null}
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              {showCustomer ? <th>לקוח</th> : null}
              <th>הצעה</th>
              <th className="num">סכום</th>
              <th>מצב</th>
              <th style={{ width: 1 }} />
              <th className="hide-sm" style={{ width: 1 }} />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonRows rows={4} cols={cols} />
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={cols} style={{ height: 'auto' }}>
                  <Empty
                    title={tab === 'waiting' ? 'אין הצעות שממתינות לאישור' : tab === 'approved' ? 'אין הצעות מאושרות שממתינות להפקה' : 'אין הצעות'}
                    body={tab === 'waiting' ? 'כל הצעת מחיר שתפיק מ"הזמנות" (או במורנינג) תופיע כאן עד שהלקוח יאשר ותופק חשבונית.' : undefined}
                  />
                </td>
              </tr>
            ) : (
              items.map((q) => {
                const b = (a: string) => busy === `${q.documentId}:${a}`;
                return (
                  <tr key={q.documentId} style={q.remindDue ? { background: 'var(--warn-soft)' } : undefined}>
                    {showCustomer ? (
                      <td className="primary">
                        <Link to={`/customers/${q.customerId}`} style={{ color: 'var(--ink)' }}>{q.customerName}</Link>
                        {q.phone ? <span className="sub"><Ltr>{q.phone}</Ltr></span> : null}
                      </td>
                    ) : null}
                    <td>
                      <button type="button" className="link-button" onClick={() => openDoc(q.documentId)}>
                        הצעת מחיר <Ltr className="tabular strong">{q.number ?? '—'}</Ltr>
                      </button>
                      <span className="sub">{fmtDate(q.issueDate)} · לפני {fmtNumber(q.daysOpen)} ימים</span>
                    </td>
                    <td className="num tabular strong">{fmtMoney(q.total)}</td>
                    <td className="small">
                      {q.state === 'waiting' ? (
                        <>
                          {q.remindDue ? <span className="badge warn">לתזכר</span> : <span className="badge">ממתינה</span>}
                          <span className="sub">
                            {q.reminders ? `${q.reminders} תזכורות · אחרונה לפני ${q.daysSinceContact} ימים` : 'עוד לא תוזכר'}
                            {q.laterSale ? ` · יצא ללקוח מסמך מכירה ב-${fmtDate(q.laterSale)} — אולי כבר טופל?` : ''}
                          </span>
                        </>
                      ) : q.state === 'approved' ? (
                        <>
                          <span className="badge ok">אושרה</span>
                          <span className="sub">{q.approvedAt ? `אושרה ${fmtDate(q.approvedAt)}` : 'אושרה'} · עדיין לא הופק מסמך</span>
                        </>
                      ) : q.state === 'invoiced' ? (
                        <>
                          <span className="badge ok">הופק</span>
                          <span className="sub">
                            {q.fulfilled.map((f) => (
                              <span key={f.orderId}>
                                {f.documentId ? (
                                  <button type="button" className="link-button" onClick={() => openDoc(f.documentId!)}>{DOC_LABEL[f.type] ?? f.type} {f.number ?? ''}</button>
                                ) : (
                                  <Link to={`/orders/${f.orderId}`}>{DOC_LABEL[f.type] ?? f.type} {f.number ?? ''}</Link>
                                )}{' '}
                              </span>
                            ))}
                          </span>
                        </>
                      ) : q.state === 'replaced' ? (
                        <>
                          <span className="badge">הוחלפה</span>
                          <span className="sub">בהצעה מעודכנת {q.replacedBy.map((r) => r.number).filter(Boolean).join(', ')}</span>
                        </>
                      ) : (
                        <span className="badge">{q.state === 'rejected' ? 'לא אושרה' : 'טופלה'}</span>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {q.state === 'waiting' ? (
                        <div className="row" style={{ gap: 4 }}>
                          <Button size="sm" variant="primary" loading={b('approve') || b('convert')} onClick={() => approveAndInvoice(q)} title="מסמן שהלקוח אישר ופותח טיוטת חשבונית מס עם אותן שורות — שם אפשר לשנות מוצרים או את סוג המסמך">אושרה → חשבונית</Button>
                          <Button size="sm" loading={b('remind')} onClick={() => remind(q, q.phone ? 'whatsapp' : 'copy')} title={q.phone ? 'פותח וואטסאפ עם הודעת תזכורת ורושם שתוזכר' : 'מעתיק את הודעת התזכורת ורושם שתוזכר'}>
                            {q.phone ? 'תזכורת בוואטסאפ' : 'העתקת תזכורת'}
                          </Button>
                        </div>
                      ) : q.state === 'approved' ? (
                        <div className="row" style={{ gap: 4 }}>
                          <Button size="sm" variant="primary" loading={b('convert')} onClick={() => convert(q, 'tax_invoice')}>הפקת חשבונית מס</Button>
                          <select
                            className="select"
                            style={{ width: 'auto', height: 30 }}
                            value=""
                            aria-label="מסמך אחר"
                            onChange={(e) => e.target.value && convert(q, e.target.value as 'tax_invoice_receipt' | 'delivery_note')}
                          >
                            <option value="">מסמך אחר…</option>
                            <option value="tax_invoice_receipt">חשבונית מס/קבלה</option>
                            <option value="delivery_note">תעודת משלוח</option>
                          </select>
                        </div>
                      ) : q.state === 'invoiced' ? null : (
                        <Button size="sm" variant="ghost" loading={b('reopen')} onClick={() => act(q, 'reopen', 'ההצעה הוחזרה למעקב')}>החזרה למעקב</Button>
                      )}
                    </td>
                    <td className="hide-sm" style={{ whiteSpace: 'nowrap' }}>
                      {q.state === 'waiting' || q.state === 'approved' ? (
                        <div className="row" style={{ gap: 4 }}>
                          <Button size="sm" variant="ghost" onClick={() => convert(q, 'quote')} title="נפתחת טיוטה עם אותן שורות — משנים, מוסיפים ומפיקים הצעה מעודכנת">שינוי / הוספה</Button>
                          {q.state === 'waiting' && q.phone ? (
                            <Button size="sm" variant="ghost" onClick={() => remind(q, 'copy')} title="מעתיק את טקסט התזכורת (למייל / SMS) ורושם שתוזכר">העתקה</Button>
                          ) : null}
                          {q.state === 'waiting' ? (
                            <Button size="sm" variant="ghost" loading={b('reject')} onClick={() => act(q, 'reject', 'ההצעה סומנה כלא אושרה')}>לא אושרה</Button>
                          ) : (
                            <Button size="sm" variant="ghost" loading={b('reopen')} onClick={() => act(q, 'reopen', 'האישור בוטל — ההצעה חזרה להמתנה')}>ביטול אישור</Button>
                          )}
                          {q.laterSale ? <Button size="sm" variant="ghost" loading={b('done')} onClick={() => act(q, 'done', 'ההצעה סומנה כטופלה')}>טופלה</Button> : null}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {tab === 'waiting' && data ? (
        <div className="panel-foot small muted">
          הצעה שלא אושרה תוך {data.remindAfterDays} ימים מהשליחה (או מהתזכורת האחרונה) מסומנת "לתזכר" ומופיעה גם בסדר העבודה היומי. "שינוי / הוספה" פותח טיוטה עם אותן שורות; כשתפיק את ההצעה המעודכנת, הישנה תיסגר כ"הוחלפה".
        </div>
      ) : null}
    </>
  );
}
